import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    ColumnDef,
} from '@tanstack/react-table';
import eventResultType from '../model/eventResultType';
import { useState, useRef, useEffect } from 'react';

/* mono columns */
const MONO = new Set(['id', 'bundle_id', 'device_id', 'detected', 'arrived']);
const NUM = new Set(['no']);

/* shortened id + hover full (title) + click to copy */
const isUuidLike = (v: unknown) => typeof v === 'string' && v.length > 24;
const shortId = (v: string) => v.slice(0, 8) + '…' + v.slice(-4);

const IdCell = ({ value }: { value: unknown }) => {
    const [copied, setCopied] = useState(false);
    if (value == null || value === '') return <span style={{ color: '#a1a1aa' }}>—</span>;
    const s = String(value);
    const display = isUuidLike(s) ? shortId(s) : s;
    const copy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try { await navigator.clipboard.writeText(s); }
        catch {
            const ta = document.createElement('textarea');
            ta.value = s; document.body.appendChild(ta); ta.select();
            try { document.execCommand('copy'); } catch {}
            ta.remove();
        }
        setCopied(true); window.setTimeout(() => setCopied(false), 1100);
    };
    return (
        <span className="bl-id" title={copied ? 'Copied!' : s + '  (クリックでコピー)'} onClick={copy}>
            <span className="bl-cell-mono">{display}</span>
            <span className={'bl-copy' + (copied ? ' ok' : '')} aria-hidden="true">
                {copied
                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>}
            </span>
        </span>
    );
};

const EventLogResult = ({ result }: { result: eventResultType[] }) => {
    const data: eventResultType[] = result;

    const mono = (c: any) => <span className="bl-cell-mono">{String(c.getValue() ?? '—')}</span>;

    const columns: ColumnDef<eventResultType, any>[] = [
        { header: "no", accessorKey: "no" },
        { header: "id", accessorKey: "id", cell: c => <IdCell value={c.getValue()} /> },
        { header: "bundle_id", accessorKey: "bundle_id", cell: c => <IdCell value={c.getValue()} /> },
        { header: "device_id", accessorKey: "device_id", cell: c => <IdCell value={c.getValue()} /> },
        { header: "event_key", accessorKey: "event_key" },
        { header: "event_value", accessorKey: "event_value" },
        { header: "detected", accessorKey: "detected", cell: mono },
        { header: "arrived", accessorKey: "arrived", cell: mono },
    ];

    const table = useReactTable({ columns, data, getCoreRowModel: getCoreRowModel() });

    const [colsOpen, setColsOpen] = useState(false);
    const popRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (popRef.current && !popRef.current.contains(e.target as Node)) setColsOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);
    const visibleCount = table.getVisibleLeafColumns().length;
    const totalCount = table.getAllLeafColumns().length;

    return (
        <div className="bl-result">
            <div className="bl-result-bar">
                <span className="bl-result-count">このページ <b>{data.length}</b> 件</span>
                <div className="bl-toolbar">
                    <div className="bl-pop-wrap" ref={popRef}>
                        <button type="button" className="bl-btn sm" title="列の表示／非表示を切り替え" onClick={() => setColsOpen(o => !o)}>
                            <ColIcon /> 表示項目 <span style={{ color: '#a1a1aa', fontFamily: 'var(--bl-mono)' }}>{visibleCount}/{totalCount}</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2, opacity: .55 }}><path d="m6 9 6 6 6-6" /></svg>
                        </button>
                        {colsOpen && (
                            <div className="bl-pop">
                                <div className="bl-pop-title">表示する列（チェックで切替）</div>
                                {table.getAllLeafColumns().map(column => (
                                    <label key={column.id} className="bl-pop-item">
                                        <input type="checkbox" checked={column.getIsVisible()} onChange={column.getToggleVisibilityHandler()} />
                                        {column.id}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="bl-table-scroll">
                <table className="bl-table">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} className={NUM.has(header.column.id) ? 'num' : ''}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className={(NUM.has(cell.column.id) ? 'num ' : '') + (MONO.has(cell.column.id) ? 'bl-cell-mono' : '')}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ColIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /><path d="M15 3v18" />
    </svg>
);

export default EventLogResult;
